import { Userpilot } from 'userpilot'
import ENV_VARS from '@/config'
import { stringifyQueryString } from '@/ports/api'
import client from '@/ports/api-client'
import { get, post, del, put } from '@/ports/api'
import prices from '@/lib/prices'

const { BACKEND_SERVER } = ENV_VARS

export const addPaymentMethod = post(`${BACKEND_SERVER}/plans/payment_methods`, {
  noLoading: true
})

const addPaymentMethodFailSilently = post(`${BACKEND_SERVER}/plans/payment_methods`, {
  noLoading: true,
  failSilently: true
})

export const deletePaymentMethod = del(
  `${BACKEND_SERVER}/plans/payment_methods/%paymentMethodId%`,
  {
    noLoading: true
  }
)

export const getPaymentMethod = get(`${BACKEND_SERVER}/plans/payment_methods`, {
  noLoading: true
})

export const purchaseStandalone = post(`${BACKEND_SERVER}/plans/subscription/standalone`, {
  noLoading: true
})

export const purchaseStandaloneWithDiscount = post(
  `${BACKEND_SERVER}/plans/subscription/standalone?couponCode=%couponCode%`,
  {
    noLoading: true
  }
)

export const boostSponsoredListing = post(`${BACKEND_SERVER}/listings/%listingId%/sponsored`, {
  noLoading: true
})

export const purchasePackage = post(`${BACKEND_SERVER}/plans/subscription/package`, {
  noLoading: true,
  failSilently: true
})
export const purchasePackageWithCoupon = post(
  `${BACKEND_SERVER}/plans/subscription/package?couponCode=%couponCode%`,
  {
    noLoading: true,
    failSilently: true
  }
)

export const addAddOn = post(`${BACKEND_SERVER}/plans/subscription/package/dry_run`, {
  noLoading: true,
  failSilently: true
})

export const addAddOnWithCoupon = post(
  `${BACKEND_SERVER}/plans/subscription/package/dry_run?couponCode=%couponCode%`,
  {
    noLoading: true,
    failSilently: true
  }
)

const updateTrustScoreFlag = put(
  `${BACKEND_SERVER}/applications/%applicationId%/trustscore_flags`,
  {
    noLoading: true
  }
)
export const consumeSyndicationCredits = post(
  `${BACKEND_SERVER}/listings/%listingId%/syndication`,
  {
    noLoading: true
  }
)

export const fetch = async () => {
  try {
    const result = await getPaymentMethod()
    if (!result) throw new Error('Empty data')
    const cards = result.body
    return cards
  } catch (err) {
    throw err
  }
}

export const add = async (data: any) => {
  const result = await addPaymentMethod(data)

  if (result?.response.ok) {
    return result?.body
  } else {
    throw new Error(result?.body.message || result?.response.statusText)
  }
}

export const addFailSilently = async (data: any) => {
  try {
    const response = await addPaymentMethodFailSilently(data)
    return response
  } catch (error) {
    throw error
  }
}

export const remove = async (paymentMethodId: string) => {
  try {
    const response = await deletePaymentMethod(undefined, { paymentMethodId })
    return response
  } catch (error) {
    throw error
  }
}

type TrustScoreInfo = {
  unlocked: number
  viewed: number
  source: string
}

export const unlock = async (applicationId: string, trustScoreInfo: TrustScoreInfo) => {
  try {
    const update = await updateTrustScoreFlag(
      {
        body: {
          trust_score_unlocked: trustScoreInfo.unlocked,
          trust_score_viewed: trustScoreInfo.viewed,
          trust_score_request_source: trustScoreInfo.source
        }
      },
      { applicationId }
    )
    return update
  } catch (error) {
    throw error
  }
}

export const purchase = async (
  paymentMethodId: string,
  applicationId: string,
  trustScoreInfo: TrustScoreInfo
) => {
  try {
    const response = await purchaseStandalone({
      plan_name: 'credit-check-single-package',
      payment_method_id: paymentMethodId,
      application_id: applicationId
    })
    const update = await updateTrustScoreFlag(
      {
        body: {
          trust_score_unlocked: trustScoreInfo.unlocked,
          trust_score_viewed: trustScoreInfo.viewed,
          trust_score_request_source: trustScoreInfo.source
        }
      },
      { applicationId }
    )
    if (response?.response.ok) {
      Userpilot.track('goal_purchase_trust_score_unlock', { price: prices.trustScore.unlock })
    }
    return { response, update }
  } catch (error) {
    throw error
  }
}

export const view = async (applicationId: string, trustScoreInfo: TrustScoreInfo) => {
  try {
    const response = await updateTrustScoreFlag(
      {
        body: {
          trust_score_unlocked: 0,
          trust_score_viewed: 1,
          trust_score_request_source: trustScoreInfo.source
        }
      },
      { applicationId }
    )
    return { response }
  } catch (error) {
    throw error
  }
}

export const getProductData = async (
  productName: string,
  opt?: { couponCode?: string; state?: string }
) => {
  // fetch single product by name via query, otherwise will return all products
  const state = opt?.state || 'BC'

  const queryString =
    productName && opt?.couponCode
      ? stringifyQueryString({ plan: productName, couponCode: opt.couponCode })
      : productName
        ? stringifyQueryString({ plan: productName })
        : ''
  try {
    return await client.get(`/catalogs/${state}${queryString}`, { noLoading: true })
  } catch (error) {
    throw error
  }
}
